import { type ReactNode, useLayoutEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSimpleLoader } from "~/features/selectionAssistant/housing/hooks/useSimpleLoader";
import Spinner from "~/common/components/Spinner/Spinner";
import { PageTitle } from "~/common/components/PageTitle/v2/PageTitle";
import type { QuoteDetailsMO, QuoteLineMO } from "~/features/orders/common/components/v2/models";
import type { PriceData } from "~/domains/pricing/types/PriceData";
import { sum } from "~/utils/PriceUtils";
import {
  getOrderLineItemSurchargeValue,
  getQuoteLineItemPriceVisibility,
  QuoteLineItemPriceVisibility,
} from "~/features/orders/common/utils/OrderLineItemUtils";
import { QuoteDetailsSummary } from "~/features/orders/quoteDetails/components/v2/QuoteDetailsSummary";
import QuoteSummaryTotals from "~/features/orders/quoteDetails/components/QuoteSummaryTotals";
import { Button, Stack, styled } from "~/common/mui";
import { C_OUTLINE_VARIANT } from "~/common/components/icon/Colors";
import { QuoteDetailsTable } from "~/features/orders/common/components/v2/QuoteTable/QuoteDetailsTable";
import { IncludesSurchargeDisclaimer } from "~/domains/pricing/components/IncludesSurchargeDisclaimer";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { fetchQuoteDetails } from "~/features/orders/quoteList/api/fetchOrderDetails/fetchQuoteDetails";
import { transformQuoteDetails } from "~/features/orders/quoteList/api/fetchOrderDetails/v2/transformQuoteDetails";
import { AccountErrorMessage } from "~/features/orders/quoteDetails/AccountErrorMessage";
import type { AddToCartApiItem } from "~/features/cart/cart/api/AddToCart";
import { sendData } from "~/features/cart/cart/api/QuoteToCart";
import { useDispatch } from "react-redux";
import { addMessage } from "~/features/toasts/redux/actions";
import { useQuoteDetailsSelectionState } from "~/features/orders/common/components/v2/QuoteTable/useQuoteDetailsSelectionState";
import { makeMapQuoteLineToATCAPIItem } from "~/features/orders/quoteList/utils";
import CartSuccessModal from "~/features/addToCart/components/AddToCartProcess/Modals/CartSuccessModal/CartSuccessModal";
import { openModal } from "~/common/utils/ModalService";
import { addBaseProductDataAction } from "~/domains/product/redux/actions";
import { DownloadContentButton } from "~/features/orders/quoteList/components/QuoteListTable/DownloadContentButton";
import { downloadFile } from "~/common/utils/ApiHelper";
import { Alert } from "~/common/mui/Alert/Alert";
import { ATCBar } from "~/features/orders/common/components/ATCBar";
import { getTotalPrice } from "~/domains/product/utils/productDescriptorUtils";
import { QuoteDetailsPageV2 } from "~/features/orders/quoteDetailsV2/QuoteDetailsPageV2";
import { OrderListDesignToggleProvider, useDesignToggle } from "~/features/orders/common/components/NewDesignToggle";
import type { PreviousAccountRollback } from "~/features/orders/quoteDetails/restorePreviousAccount";

const CGrid = styled("div")({
  display: "grid",
  gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
  columnGap: "var(--gutter)",
});

const PageRoot = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(8),
}));

const StyledPageTitle = styled(PageTitle)(({ theme }) => ({
  gridTemplateRows: "[header-start] auto [header-end] 1fr",
  "@media print": {
    fontSize: "24px",
    lineHeight: "32px",
  },
}));

const HeaderGrid = styled("div")({
  display: "grid",
  gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
  columnGap: "var(--gutter)",
});

const SummarySpan = styled("div")({
  gridColumn: "1 / span 8",
});

const TotalsSpan = styled("div")({
  gridColumn: "span 4 / -1",
  alignSelf: "flex-end",
});

const HideInPrint = styled("div")({
  display: "contents",
  "@media print": {
    display: "none",
  },
});

const TableContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
  columnGap: "var(--gutter)",
  rowGap: theme.spacing(4),
}));

const TableTitle = styled("div")({
  gridColumn: "span 6",
  alignSelf: "center",
});

const UpperActionButtons = styled("div")({
  gridColumn: "span 6",
  alignSelf: "center",
  justifySelf: "flex-end",
});

const DisabledAtcMessage = styled("div")(({ theme }) => ({
  gridColumnEnd: "span 12",
  justifySelf: "flex-end",
  marginTop: `calc(${theme.spacing(1)} - 32px)`,
}));

const DownloadPdfSpan = styled("div")({
  gridColumn: "-4 / -1",
  justifySelf: "flex-end",
  alignSelf: "center",
});

const TableSpan = styled("div")({
  gridColumn: "1 / -1",
});

const FooterSeparator = styled("hr")({
  margin: 0,
  borderBottom: "3px solid",
  borderColor: C_OUTLINE_VARIANT,
  gridColumn: "1 / -1",
});

const TotalsSeparator = styled("hr")({
  margin: 0,
  borderBottom: "1px solid",
  borderColor: C_OUTLINE_VARIANT,
  gridColumn: "span 4 / -1",
});

const FooterGrid = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
  columnGap: "var(--gutter)",
  rowGap: theme.spacing(4),
  "@media print": {
    display: "none",
  },
}));

const SurchargeDisclaimer = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(-4),
}));

const LowerActionButtons = styled("div")({
  gridColumn: "span 6 / -1",
  justifySelf: "flex-end",
});

const CheckMessagesSpan = styled("div")({
  gridColumn: "1 / span 6",
  alignSelf: "flex-start",
});

export interface QuoteDetailsPageProps {
  previousAccount: PreviousAccountRollback | null;
}

export function createQuoteDetailsPage(data: QuoteDetailsPageProps) {
  const params = new URLSearchParams(window.location.search);
  const quoteNumber = params.get("id") || "";
  return (
    <>
      {data.previousAccount && <AccountErrorMessage previousAccount={data.previousAccount} />}
      <OrderListDesignToggleProvider>
        <QuoteDetailsPage quoteNumber={quoteNumber} />
      </OrderListDesignToggleProvider>
    </>
  );
}

interface Props {
  quoteNumber: string;
}

export const QuoteDetailsPage = ({ quoteNumber }: Props) => {
  const { isNewDesignEnabled } = useDesignToggle();

  if (isNewDesignEnabled) {
    return <QuoteDetailsPageV2 quoteNumber={quoteNumber} />;
  }

  return <QuoteDetailsPageLegacy quoteNumber={quoteNumber} />;
};

const QuoteDetailsPageLegacy = ({ quoteNumber }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { data: quote, state } = useSimpleLoader(async () => {
    const dto = await fetchQuoteDetails(quoteNumber);

    const data = transformQuoteDetails(dto);
    dispatch(addBaseProductDataAction(data.quoteLines.map((entry) => entry.product)));
    return data;
  }, [quoteNumber]);

  let content: ReactNode = null;
  if (!quoteNumber) {
    content = (
      <Alert className="mt-3" severity="error">
        {t("quoteDetailsPage.error.missingOrder")}
      </Alert>
    );
  } else if (state === "loading") {
    content = <Spinner className="mt-3" />;
  } else if (state === "error" || !quote) {
    content = (
      <Alert className="mt-3" severity="error">
        {t("quoteDetailsPage.error.general")}
      </Alert>
    );
  } else {
    content = <LoadedQuoteDetailsPage quote={quote} />;
  }
  return (
    <div className="scha__order-details-page">
      <StyledPageTitle>{t("quoteDetailsPage.title")}</StyledPageTitle>
      {content}
    </div>
  );
};

interface selectedATCItemsProps {
  selectedLineItems: number;
  selectedQuantity: number;
  totalPrice: PriceData;
}

const selectedATCItemsDefaultValue = {
  selectedLineItems: 0,
  selectedQuantity: 0,
  totalPrice: { value: 0, currencyIso: "" },
};

const LoadedQuoteDetailsPage = ({ quote }: { quote: QuoteDetailsMO }) => {
  const { t } = useTranslation();

  const cboxProps = useQuoteDetailsSelectionState();
  useLayoutEffect(() => {
    cboxProps.init(quote);
  }, [quote]);

  const selectedLineItems = useMemo(() => {
    return quote.quoteLines.filter((line) => cboxProps.selectedLineNos[line.lineNo]);
  }, [quote.quoteLines, cboxProps.selectedLineNos]);

  const selectedATCItems = useMemo(() => getSelectedATCItems(selectedLineItems), [selectedLineItems]);

  const { addSelectedQuotesToCart, isSending } = useAddToCartLogic({
    quote,
    selectedLineNos: cboxProps.selectedLineNos,
  });

  const totalSurcharge = calculateTotalSurcharge(quote);
  const containsSurcharge = totalSurcharge.value > 0;

  const canPurchase = userHasPermission(Permission.PLACE_ORDER);

  const disableCheckoutBtn = quote.disabled || !cboxProps.isAnythingSelected;
  const showDisabledMsg = disableCheckoutBtn && cboxProps.isAnythingSelected;

  // In case we have more reasons to show checkboxes in the future
  const showCheckboxes = canPurchase;

  const actionButtons = (
    <>
      <Button
        data-test-id={"add-all-to-cart"}
        disabled={disableCheckoutBtn || isSending}
        onClick={addSelectedQuotesToCart}
      >
        {t("quoteDetailsPage.checkout")}
      </Button>
    </>
  );

  const disabledButtonMessage = showDisabledMsg && (
    <DisabledAtcMessage>
      <Alert severity="info">{t("quoteDetailsPage.summary.disabledBtnMsg")}</Alert>
    </DisabledAtcMessage>
  );

  const totals = (
    <QuoteSummaryTotals
      totalSelectableWeight={quote.totalSelectableWeight}
      totalSelectableValue={quote.totalSelectablePrice}
      totalValue={quote.totalPrice}
      surcharge={containsSurcharge ? totalSurcharge : undefined}
    />
  );

  return (
    <PageRoot>
      <HeaderGrid>
        <SummarySpan>
          <QuoteDetailsSummary quote={quote} />
        </SummarySpan>
        <TotalsSpan>{totals}</TotalsSpan>
      </HeaderGrid>
      <TableContainer>
        <HideInPrint>
          <TableTitle>
            <h3 className="m-0">{t("quoteDetailsPage.table.title")}</h3>
          </TableTitle>
          <UpperActionButtons>{actionButtons}</UpperActionButtons>
          {disabledButtonMessage}

          {quote.quoteDocumentUrl && (
            <DownloadPdfSpan>
              <DownloadInvoices quoteDocumentUrl={quote.quoteDocumentUrl} soNumber={quote.soNumber} />
            </DownloadPdfSpan>
          )}

          <TableSpan>
            <QuoteDetailsTable quoteLines={quote.quoteLines} showCheckboxes={showCheckboxes} {...cboxProps} />
          </TableSpan>
        </HideInPrint>
      </TableContainer>

      <FooterGrid>
        <FooterSeparator />
        <TotalsSpan>{totals}</TotalsSpan>
        <TotalsSeparator />
        <LowerActionButtons>{actionButtons}</LowerActionButtons>
        {disabledButtonMessage}
      </FooterGrid>
      {<IncludesSurchargeDisclaimer hasSurcharge={containsSurcharge} />}
      {selectedATCItems.selectedLineItems > 0 && (
        <ATCBar
          selectedLineItems={selectedATCItems.selectedLineItems}
          selectedQuantity={selectedATCItems.selectedQuantity}
          totalPrice={selectedATCItems.totalPrice}
          onAddToCart={addSelectedQuotesToCart}
          loading={isSending}
        />
      )}
    </PageRoot>
  );
};

function calculateTotalSurcharge(data: QuoteDetailsMO): PriceData {
  return sum(
    ...data.quoteLines
      .filter((lineItem) => {
        return (
          getQuoteLineItemPriceVisibility({
            lineItemStatus: lineItem.status,
            quoteStatus: data.status,
          }) === QuoteLineItemPriceVisibility.VISIBLE
        );
      })
      .flatMap((lineItem) => lineItem.parts)
      .map((part) => getOrderLineItemSurchargeValue(part)),
  );
}

const DownloadInvoices = ({
  quoteDocumentUrl,
  soNumber,
  className,
}: {
  quoteDocumentUrl?: string;
  soNumber: string;
  className?: string;
}) => {
  const { t } = useTranslation();
  const [loadingDownload, setLoadingDownload] = useState(false);

  const handleDownload = async () => {
    setLoadingDownload(true);
    if (quoteDocumentUrl && soNumber) {
      await downloadFile(`${quoteDocumentUrl}?id=${soNumber}`);
    }
    setLoadingDownload(false);
  };

  return (
    <Stack direction={"row"} gap={2} className={className}>
      <span className="f-label-color">{t("quoteDetailsPage.downloadFile.title")}</span>
      <DownloadContentButton
        label={t("downloadFile.type.pdf")}
        onClick={handleDownload}
        isLoading={loadingDownload}
        labelHover={t("quoteListPage.downloadDetails.helperLabel")}
      />
    </Stack>
  );
};

const useAddToCartLogic = ({
  quote,
  selectedLineNos,
}: {
  quote: QuoteDetailsMO;
  selectedLineNos: Record<string, boolean>;
}) => {
  const [isSending, setIsSending] = useState(false);
  const dispatch = useDispatch();

  const addSelectedQuotesToCart = async () => {
    // TODO: There is no logic for bundled quote & alternatives.
    // For now we assume that all need to be selected for bundle quote to work
    const selectedLineItems = quote.quoteLines.filter((line) => selectedLineNos[line.lineNo]);

    const isAllSelected = selectedLineItems.length === quote.quoteLines.length;
    const isAllDisabled = quote.quoteLines.every((line) => line.disabled);

    if (quote.isBundledQuote && !isAllSelected) {
      // TODO
      throw new Error("Bundled quote not fully selected");
    }

    if (selectedLineItems.flatMap((line) => line.parts).some((part) => !part.price)) {
      throw new Error("Some parts do not have a price");
    }

    if (quote.disabled || isAllDisabled) {
      return;
    }

    setIsSending(true);

    const atcItems: AddToCartApiItem[] = selectedLineItems.flatMap(makeMapQuoteLineToATCAPIItem());

    try {
      const response = await sendData({
        quoteNumber: quote.soNumber,
        items: atcItems,
      });
      if (response.ok) {
        openModal("add-to-cart-dialog", (close) => (
          <CartSuccessModal onClose={close} page="QUOTE_DETAILS" responseItems={response.responseItems} />
        ));
        //dispatch(addMessage("info", "page.quoteDetails.addToCart.success", "translation"));
      } else {
        dispatch(addMessage("error", "page.quoteDetails.errorSending", "translation"));
      }
    } catch (e) {
      console.error(e);
      dispatch(addMessage("error", "page.quoteDetails.errorSending", "translation"));
    } finally {
      setIsSending(false);
    }
  };

  return {
    isSending,
    addSelectedQuotesToCart,
  };
};

const getSelectedATCItems = (selectedLineItems: QuoteLineMO[]): selectedATCItemsProps => {
  if (selectedLineItems.length === 0) {
    return selectedATCItemsDefaultValue;
  }

  const totalPieces = selectedLineItems.reduce((accumulator, line) => {
    return accumulator + line.parts.reduce((acc, part) => acc + part.confirmedQuantity, 0);
  }, 0);

  const currencyIso = selectedLineItems[0]?.parts[0]?.price?.currencyIso;

  const totalPrice = selectedLineItems.reduce((accumulator, line) => {
    return (
      accumulator +
      line.parts.reduce((acc, part) => {
        const priceData = getTotalPrice(part.price, part.confirmedQuantity);
        if (priceData.currencyIso !== currencyIso) {
          throw new Error("Mismatched currencies in selected items");
        }
        return acc + priceData.value;
      }, 0)
    );
  }, 0);

  return {
    selectedLineItems: selectedLineItems.length,
    selectedQuantity: totalPieces,
    totalPrice: {
      currencyIso: currencyIso ?? "",
      value: totalPrice,
    },
  };
};
